/**
 * Shared appointment type constants used by both CrawlEditMode and
 * AppointmentTypesFeesStep.
 *
 * Category keys must match the backend seed data categories in
 * db/seeds/seed_45_appointment_types.py.
 */

/** Human-readable labels for appointment type categories. */
export const CATEGORY_LABELS: Record<string, string> = {
  therapy_individual: 'Individual Therapy',
  therapy_couples_family: 'Couples / Family Therapy',
  psychiatry: 'Psychiatry',
  assessment: 'Assessment',
  kap: 'Ketamine-Assisted Psychotherapy (KAP)',
  group_therapy: 'Group Therapy',
  neurofeedback: 'Neurofeedback',
  crisis: 'Crisis',
  intensives: 'Intensives',
  consultation: 'Consultation / Supervision',
  other_wellness: 'Other Wellness',
  admin_client_contact: 'Admin Client Contact',
  admin_time: 'Admin Time',
  internal_meeting: 'Internal Meeting',
  break: 'Break',
  time_off: 'Time Off',
  addon_cpt_codes: 'Add-On Codes',
  addon_fees: 'Add-On Fees',
};

/**
 * High-level grouping of granular categories into Clinical vs Non-Clinical.
 * Mirrors APT_TYPE_TO_ENCOUNTER_CATEGORY in appointmentFormUtils.ts.
 */
export const CATEGORY_GROUP: Record<string, 'clinical' | 'admin_client_contact' | 'admin'> = {
  therapy_individual: 'clinical',
  therapy_couples_family: 'clinical',
  kap: 'clinical',
  psychiatry: 'clinical',
  assessment: 'clinical',
  group_therapy: 'clinical',
  neurofeedback: 'clinical',
  crisis: 'clinical',
  intensives: 'clinical',
  consultation: 'clinical',
  addon_cpt_codes: 'clinical',
  addon_fees: 'clinical',
  other_wellness: 'clinical',
  admin_client_contact: 'admin_client_contact',
  admin_time: 'admin',
  internal_meeting: 'admin',
  break: 'admin',
  time_off: 'admin',
};

/**
 * Internal-only categories — never bookable by clients. Excluded from every
 * client-booking config surface (widget settings, portal booking, packet
 * assignment). `admin_client_contact` stays: it is patient-facing.
 */
export const NON_CLIENT_BOOKING_CATEGORIES = new Set([
  ...Object.keys(CATEGORY_GROUP).filter((c) => CATEGORY_GROUP[c] === 'admin'),
  'external_meeting',
  'misc_appt',
  'calendar_note',
]);

/** Human-readable labels for high-level category groups. */
export const CATEGORY_GROUP_LABELS: Record<string, string> = {
  clinical: 'Clinical',
  admin_client_contact: 'Admin Client Contact',
  admin: 'Admin / Scheduling',
};

/** Color-coded backgrounds for service group headers (shared by wizard + admin). */
export const SERVICE_GROUP_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  clinical:             { bg: '#e3f2fd', border: '#90caf9', text: '#1565c0' },
  admin_client_contact: { bg: '#fff3e0', border: '#ffb74d', text: '#e65100' },
  admin:                { bg: '#f5f5f5', border: '#bdbdbd', text: '#616161' },
};

/** Short descriptions for each service group (displayed under headers). */
export const SERVICE_GROUP_DESCRIPTIONS: Record<string, string> = {
  clinical: 'Billable patient appointments. CPT codes optional for non-clinical services.',
  admin_client_contact: 'Patient-facing appointments conducted by admins — e.g. screening calls.',
  admin: 'Internal operations — meetings, time off, breaks. Not patient-facing.',
};

/** Selectable clinical categories for appointment type assignment. */
export const CLINICAL_CATEGORIES: { value: string; label: string }[] = [
  { value: 'therapy_individual', label: 'Individual Therapy' },
  { value: 'therapy_couples_family', label: 'Couples / Family Therapy' },
  { value: 'psychiatry', label: 'Psychiatry' },
  { value: 'assessment', label: 'Assessment' },
  { value: 'kap', label: 'KAP' },
  { value: 'group_therapy', label: 'Group Therapy' },
  { value: 'neurofeedback', label: 'Neurofeedback' },
  { value: 'crisis', label: 'Crisis' },
  { value: 'intensives', label: 'Intensives' },
  { value: 'addon_cpt_codes', label: 'Add-On Codes' },
  { value: 'addon_fees', label: 'Add-On Fees' },
];

/**
 * Full list of selectable categories grouped by high-level bucket.
 * Used by admin config UIs where users need to move a type between any
 * category (clinical or admin/scheduling) — e.g. changing "Lunch Break"
 * from ``break`` to ``admin_time``.
 */
export const ALL_CATEGORY_OPTIONS: { group: string; value: string; label: string }[] = [
  { group: 'Clinical', value: 'therapy_individual', label: 'Individual Therapy' },
  { group: 'Clinical', value: 'therapy_couples_family', label: 'Couples / Family Therapy' },
  { group: 'Clinical', value: 'psychiatry', label: 'Psychiatry' },
  { group: 'Clinical', value: 'assessment', label: 'Assessment' },
  { group: 'Clinical', value: 'kap', label: 'KAP' },
  { group: 'Clinical', value: 'group_therapy', label: 'Group Therapy' },
  { group: 'Clinical', value: 'neurofeedback', label: 'Neurofeedback' },
  { group: 'Clinical', value: 'crisis', label: 'Crisis' },
  { group: 'Clinical', value: 'intensives', label: 'Intensives' },
  { group: 'Clinical', value: 'addon_cpt_codes', label: 'Add-On Codes' },
  { group: 'Clinical', value: 'addon_fees', label: 'Add-On Fees' },
  { group: 'Clinical', value: 'consultation', label: 'Consultation / Supervision' },
  { group: 'Clinical', value: 'other_wellness', label: 'Other Wellness' },
  { group: 'Admin Client Contact', value: 'admin_client_contact', label: 'Admin Client Contact' },
  { group: 'Admin / Scheduling', value: 'internal_meeting', label: 'Internal Meeting' },
  { group: 'Admin / Scheduling', value: 'admin_time', label: 'Admin Time' },
  { group: 'Admin / Scheduling', value: 'break', label: 'Break' },
  { group: 'Admin / Scheduling', value: 'time_off', label: 'Time Off' },
];

/** @deprecated Non-clinical was unified into clinical. Kept for backward-compat. */
export const NON_CLINICAL_PATIENT_CATEGORIES: { value: string; label: string }[] = [
  { value: 'consultation', label: 'Consultation / Supervision' },
  { value: 'other_wellness', label: 'Other Wellness' },
];

/** Non-billable category keys — these show "N/A" for fees. */
export const NON_BILLABLE_CATEGORIES = new Set([
  'admin_time',
  'internal_meeting',
  'break',
  'time_off',
  'admin_client_contact',
]);

/**
 * Categories that have fees per duration but do NOT use CPT codes.
 * These types show fee / buffer / toggle configuration but skip
 * CPT method selection, CPT code columns, and add-on code sections.
 */
export const NO_CPT_CATEGORIES = new Set([
  'consultation',
  'other_wellness',
  'admin_client_contact',
]);

/**
 * Mapping from service type keys (selected in the Services step) to the
 * appointment type names they include.  Must stay in sync with the backend
 * SERVICE_TYPE_DEFINITIONS in services/admin/onboarding_service.py.
 */
export const SERVICE_TYPE_APT_NAMES: Record<string, string[]> = {
  therapy: [
    'Individual Therapy',
    'Individual Therapy Intake',
    'Crisis Session',
    'Family Therapy',
    'Couples/Family Intake',
    'Consultation',
  ],
  assessment: [
    'Assessment Intake',
    'Assessment Feedback',
    'Test Administration',
    'Collateral Contact',
    'Records Review',
    'Scoring & Interpretation',
    'Report Writing',
  ],
  psychiatry: [
    'Psychiatry Intake',
    'Psychiatry Follow-Up',
  ],
  kap: [
    'KAP Intake', 'KAP Experiential Session', 'KAP Prep/Integration Session',
  ],
  neurofeedback: ['Neurofeedback Session', 'Neurofeedback Intake'],
  group_therapy: ['Group Therapy'],
  yoga: ['Yoga Session', 'Meditation / Mindfulness', 'Wellness Session (Other)'],
  consultation: ['Consultation', 'Clinical Supervision'],
  intensives: ['Intensive Therapy'],
};

/** Admin/scheduling types that are always enabled regardless of selected services. */
export const ALWAYS_ENABLED_TYPES = new Set([
  'Screening Call', 'Admin Time', 'Internal Meeting',
  'Scheduled Break', 'Time Off', 'Free Initial Consultation',
]);

/**
 * Maps opener ServiceCategory selections to SERVICE_TYPE_APT_NAMES keys.
 * Used by useCanonicalState to pre-filter which types are enabled based
 * on what the user said they offer in the conversational opener.
 */
export const OPENER_CATEGORY_TO_SERVICE_KEYS: Record<string, string[]> = {
  therapy: ['therapy', 'group_therapy', 'intensives'],
  psychiatry: ['psychiatry'],
  assessment: ['assessment'],
  kap: ['kap'],
  neurofeedback: ['neurofeedback'],
  wellness: ['yoga', 'consultation'],
};

/**
 * Seed list of canonical appointment types with their categories.
 * Must stay in sync with APPOINTMENT_TYPES_DATA in
 * db/seeds/seed_45_appointment_types.py.
 */
export const CANONICAL_SEED_TYPES: { name: string; category: string }[] = [
  // Individual Therapy
  { name: 'Individual Therapy', category: 'therapy_individual' },
  { name: 'Individual Therapy Intake', category: 'therapy_individual' },
  // Couples / Family Therapy
  { name: 'Family Therapy', category: 'therapy_couples_family' },
  { name: 'Couples/Family Intake', category: 'therapy_couples_family' },
  // Group Therapy
  { name: 'Group Therapy', category: 'group_therapy' },
  // Psychiatry
  { name: 'Psychiatry Intake', category: 'psychiatry' },
  { name: 'Psychiatry Follow-Up', category: 'psychiatry' },
  // KAP
  { name: 'KAP Intake', category: 'kap' },
  { name: 'KAP Experiential Session', category: 'kap' },
  { name: 'KAP Prep/Integration Session', category: 'kap' },
  // Assessment (workflow stages)
  { name: 'Assessment Intake', category: 'assessment' },
  { name: 'Test Administration', category: 'assessment' },
  { name: 'Collateral Contact', category: 'assessment' },
  { name: 'Records Review', category: 'assessment' },
  { name: 'Scoring & Interpretation', category: 'assessment' },
  { name: 'Report Writing', category: 'assessment' },
  { name: 'Assessment Feedback', category: 'assessment' },
  // Crisis
  { name: 'Crisis Session', category: 'crisis' },
  // Intensives
  { name: 'Intensive Therapy', category: 'intensives' },
  // Neurofeedback
  { name: 'Neurofeedback Session', category: 'neurofeedback' },
  { name: 'Neurofeedback Intake', category: 'neurofeedback' },
  // Wellness Services
  { name: 'Yoga Session', category: 'other_wellness' },
  { name: 'Meditation / Mindfulness', category: 'other_wellness' },
  { name: 'Wellness Session (Other)', category: 'other_wellness' },
  // Consultation / Supervision
  { name: 'Consultation', category: 'consultation' },
  { name: 'Clinical Supervision', category: 'consultation' },
  // Admin Client Contact
  { name: 'Screening Call', category: 'admin_client_contact' },
  // Wellness (other)
  { name: 'Free Initial Consultation', category: 'other_wellness' },
  // Admin / Scheduling
  { name: 'Admin Time', category: 'admin_time' },
  { name: 'Internal Meeting', category: 'internal_meeting' },
  { name: 'Scheduled Break', category: 'break' },
  { name: 'Time Off', category: 'time_off' },
];
